import {
  ActionIcon,
  Chip,
  createStyles,
  Group,
  Paper,
  ScrollArea,
  SimpleGrid,
  Skeleton,
  Space,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';

import React, { useContext, useState } from 'react';
import IVehicle from '../../models/IVehicle';
import { VehicleCreateDialog } from './VehicleCreateDialog';
import { VehicleEditDialog } from './VehicleEditDialog';
import { VehicleDeleteDialog } from './VehicleDeleteDialog';
import { AbstractContextType } from '../../context/AbstractProvider';
import { VehicleContext } from '../../context/ContextWrapper';
import { useTranslation } from 'react-i18next';
import { VehicleTypeDialog } from './vehicletypes/VehicleTypeDialog';
import IVehicleType from '../../models/IVehicleType';
import IPackageUnit from '../../models/IPackageUnit';
import moment from 'moment/moment';
import cx from 'clsx';
import { sortVehicle } from '../../utility/sorter';
import { VehicleStatusElement } from './VehicleStatusElement';
import { searchVehicle } from '../../utility/search';
import { X } from 'tabler-icons-react';
import { VehicleRestoreDialog } from './VehicleRestoreDialog';

const useStyles = createStyles((theme) => ({
  header: {
    zIndex: 1,
    position: 'sticky',
    top: 0,
    backgroundColor:
      theme.colorScheme === 'dark'
        ? theme.colors.dark[6]
        : theme.colors.gray[0],
    transition: 'box-shadow 150ms ease',
    after: {
      content: "''",
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      borderBottom: 'rem(1px) solid',
    },
  },
  scrolled: {
    boxShadow: '0 0.0625rem 0.1875rem rgba(0, 0, 0, 0.05)',
  },
  highlightedRow: {
    backgroundColor:
      theme.colorScheme === 'dark'
        ? `${theme.colors.blue[9]} !important`
        : `${theme.colors.blue[1]} !important`,
    transition: 'background-color 0.3s ease',
  },
}));

export default function VehicleTable() {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const ctx = useContext(VehicleContext) as AbstractContextType<IVehicle>;
  const [scrolled, setScrolled] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [lastActionId, setLastActionId] = useState<number | null>(null);

  const onRowClick = (id: number) => {
    setLastActionId(id);
  };

  function createRows(elements: IVehicle[]) {
    return elements
      .filter((element) => {
        return searchVehicle(element, searchValue);
      })
      .sort((a, b) => sortVehicle(a, b))
      .map((element) => {
        return (
          <tr
            key={element.id}
            onClick={() => onRowClick(element.id)}
            className={
              lastActionId === element.id ? classes.highlightedRow : ''
            }
            style={{ cursor: 'pointer' }}
          >
            <td>{element.radio_call_name}</td>
            <td>{(element.type_of as IVehicleType)?.abbreviation}</td>
            <td>{(element.type_of as IVehicleType)?.crew}</td>
            <td>
              <Chip.Group>
                {(element.type_of as IVehicleType)?.has.map((e) => (
                  <Chip
                    key={e}
                    size="xs"
                    color="blue"
                    checked={true}
                    variant="filled"
                  >
                    {e}
                  </Chip>
                ))}
              </Chip.Group>
            </td>
            <td>
              <div style={{ display: 'flex' }}>
                <Text>
                  {element.max_cargo}{' '}
                  {(element.package_unit as IPackageUnit)?.name}
                  {','}
                </Text>
                <Space w="xl" />
                <Text>
                  {element.max_weight} {element.weight_unit}
                </Text>
              </div>
            </td>
            <td>
              <VehicleStatusElement status={element.status} />
            </td>
            <td>{element.phone}</td>
            <td>
              {moment.utc(element.available_from).local().format('DD.MM.YYYY')}
            </td>
            <td>
              <Group spacing="xs">
                <VehicleEditDialog vehicle={element} />
                <VehicleRestoreDialog vehicle={element} />
                <VehicleDeleteDialog vehicle={element} />
              </Group>
            </td>
          </tr>
        );
      });
  }

  return (
    <Paper withBorder radius="md" p="xs">
      <Stack>
        <Title order={2}>Fahrzeuge</Title>
        <SimpleGrid cols={2}>
          <Group>
            <VehicleCreateDialog />
          </Group>
          <Group position="right">
            <VehicleTypeDialog />
          </Group>
        </SimpleGrid>
        <TextInput
          placeholder={t('main.Search')}
          radius="md"
          size="md"
          rightSection={
            searchValue && (
              <ActionIcon onClick={() => setSearchValue('')}>
                {' '}
                <X />{' '}
              </ActionIcon>
            )
          }
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
        />
        <ScrollArea
          h={'85vh'}
          onScrollPositionChange={({ y }) => setScrolled(y !== 0)}
        >
          <Table striped verticalSpacing="xs" fontSize="md">
            <thead
              className={cx(classes.header, { [classes.scrolled]: scrolled })}
            >
              <tr>
                <th>{t('VehicleForm.RadioCallName')}</th>
                <th>{t('VehicleTable.Type')}</th>
                <th>{t('VehicleTable.Crew')}</th>
                <th>{t('VehicleTable.Has')}</th>
                <th>{t('VehicleTable.MaxLoad')}</th>
                <th>{t('VehicleTable.Status')}</th>
                <th>{t('VehicleTable.PhoneNumber')}</th>
                <th>{t('VehicleTable.AvailableFrom')}</th>
                <th>{t('VehicleTable.Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {createRows(ctx.entities)}
              {ctx.loading &&
                [1, 2].map((id) => {
                  return (
                    <tr key={id}>
                      <td>
                        <Skeleton height={12} mt={6} width="50%" radius="xl" />
                      </td>
                      <td>
                        <Skeleton height={12} mt={6} width="50%" radius="xl" />
                      </td>
                      <td>
                        <Skeleton height={12} mt={6} width="70%" radius="xl" />
                      </td>
                      <td>
                        <Skeleton height={12} mt={6} width="20%" radius="xl" />
                      </td>
                      <td>
                        <Skeleton height={12} mt={6} width="60%" radius="xl" />
                      </td>
                      <td>
                        <Skeleton height={12} mt={6} width="70%" radius="xl" />
                      </td>
                      <td>
                        <Skeleton height={12} mt={6} width="20%" radius="xl" />
                      </td>
                      <td>
                        <Skeleton height={12} mt={6} width="60%" radius="xl" />
                      </td>
                      <td>
                        <Skeleton height={12} mt={6} width="60%" radius="xl" />
                      </td>
                      <td>
                        <Skeleton height={12} mt={6} width="40%" radius="xl" />
                      </td>
                      <td>
                        <Skeleton height={12} mt={6} width="40%" radius="xl" />
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </Table>
        </ScrollArea>
        {!ctx.loading && ctx.entities.length == 0 && (
          <Text align="center">{t('VehicleTable.NoVehiclesAvailable')}</Text>
        )}
      </Stack>
    </Paper>
  );
}
